import { useState } from "react";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { API } from "../lib/api";

export const mediaUrl = (path) => `${API}/gallery/media/${path}`;
const STAGE_LABEL = { before: "Before", during: "During", after: "After" };
const STAGE_STYLE = {
  before: "bg-charcoal/85 text-white", during: "bg-gold text-charcoal", after: "bg-emerald text-white",
};

export function MediaThumb({ item, className = "" }) {
  if (item.type === "video") {
    return (
      <div className={`relative ${className}`}>
        <video src={mediaUrl(item.path)} className="w-full h-full object-cover" muted />
        <span className="absolute inset-0 grid place-items-center"><Play className="w-8 h-8 text-white drop-shadow" /></span>
      </div>
    );
  }
  return <img src={mediaUrl(item.path)} alt="project" className={`object-cover ${className}`} />;
}

export function ProjectCard({ project, onOpen }) {
  const cover = project.media?.[0];
  return (
    <button onClick={() => onOpen(project)} data-testid={`project-card-${project.id}`} className="group text-left w-full">
      <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-charcoal/5">
        {cover ? <MediaThumb item={cover} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />
          : <div className="w-full h-full grid place-items-center text-olive/40">No media yet</div>}
        {project.media?.length > 1 && (
          <span className="absolute bottom-3 right-3 text-xs font-700 bg-black/60 text-white px-2 py-1 rounded-sm">
            {project.media.length} photos
          </span>
        )}
      </div>
      <div className="mt-4">
        {project.category && <span className="text-xs tracking-[0.18em] uppercase font-600 text-gold">{project.category}</span>}
        <h3 className="font-display text-xl font-600 text-charcoal mt-1">{project.title}</h3>
        {project.description && <p className="text-olive text-sm mt-1 line-clamp-2">{project.description}</p>}
      </div>
    </button>
  );
}

export function ProjectModal({ project, onClose }) {
  const media = project.media || [];
  const [i, setI] = useState(0);
  const cur = media[i];
  const prev = () => setI((x) => (x - 1 + media.length) % media.length);
  const next = () => setI((x) => (x + 1) % media.length);

  return (
    <div className="fixed inset-0 z-[80] bg-black/90 flex flex-col" data-testid="project-modal">
      <div className="flex items-center justify-between px-6 py-4 text-white">
        <div>
          <p className="text-xs uppercase tracking-wide text-gold">{project.category}</p>
          <h3 className="font-display text-xl font-600">{project.title}</h3>
        </div>
        <button onClick={onClose} data-testid="project-modal-close" className="text-white/80 hover:text-white"><X className="w-7 h-7" /></button>
      </div>

      <div className="flex-1 relative flex items-center justify-center px-4 min-h-0">
        {cur && (cur.type === "video" ? (
          <video src={mediaUrl(cur.path)} controls autoPlay className="max-h-full max-w-full rounded-sm" data-testid="project-video" />
        ) : (
          <img src={mediaUrl(cur.path)} alt="project media" className="max-h-full max-w-full object-contain rounded-sm" />
        ))}
        {cur && <span className={`absolute top-3 left-6 text-xs font-700 uppercase px-2.5 py-1 rounded-sm ${STAGE_STYLE[cur.stage] || "bg-white text-charcoal"}`}>{STAGE_LABEL[cur.stage] || cur.stage}</span>}
        {media.length > 1 && (
          <>
            <button onClick={prev} data-testid="project-prev" className="absolute left-4 grid place-items-center w-11 h-11 rounded-full bg-white/15 text-white hover:bg-white/30"><ChevronLeft className="w-6 h-6" /></button>
            <button onClick={next} data-testid="project-next" className="absolute right-4 grid place-items-center w-11 h-11 rounded-full bg-white/15 text-white hover:bg-white/30"><ChevronRight className="w-6 h-6" /></button>
          </>
        )}
      </div>

      <div className="flex gap-2 overflow-x-auto px-6 py-4">
        {media.map((m, idx) => (
          <button key={m.path} onClick={() => setI(idx)} className={`relative shrink-0 w-20 h-16 rounded-sm overflow-hidden border-2 ${idx === i ? "border-gold" : "border-transparent opacity-70"}`}>
            <MediaThumb item={m} className="w-full h-full" />
          </button>
        ))}
      </div>
    </div>
  );
}
